import React, { useId, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { T } from "../app/tokens.js";
import { KpiBandCollectionContext, KpiBandPresentationContext, useKpiBand } from "../contexts/KpiBandContext.jsx";
import { KpiTileRow } from "../features/plan/KpiTileRow.jsx";
import "./KpiBand.css";

// Children are explicit metric components (KpiTile or scenario adapters).
// Only the presentation changes; scope, permissions and calculations stay
// with the owning surface. Only explicitly approved summaries opt in.
export function KpiBand({ testId, children, ariaLabel = "Collection metrics" }) {
  const preference = useKpiBand();
  const condensed = preference?.mode === "CONDENSED";
  const id = useId();
  const toggleRef = useRef(null);
  const expandFromBackground = () => {
    toggleRef.current?.focus();
    preference?.toggle();
  };
  return (
    <KpiBandPresentationContext.Provider value={condensed}>
    <KpiBandCollectionContext.Provider value>
      <section data-kpi-band="" data-kpi-mode={condensed ? "CONDENSED" : "FULL"} aria-label={ariaLabel} style={{ minWidth: 0 }}>
        <div onClick={condensed ? (event) => {
          if (event.target === event.currentTarget) expandFromBackground();
        } : undefined}
          style={{ display: "flex", alignItems: "stretch", gap: condensed ? 0 : 12, minWidth: 0, ...(condensed ? { background: T.white, border: `1px solid ${T.border}`, borderRadius: 11, minHeight: T.toolbarRowH, cursor: "pointer" } : {}) }}>
          <KpiTileRow testId={testId} collection condensed={condensed} contentId={id} onExpand={expandFromBackground}>{children}</KpiTileRow>
          <button ref={toggleRef} type="button" aria-label={condensed ? "Expand KPI band" : "Collapse KPI band"} aria-expanded={!condensed} aria-controls={id}
            onClick={preference?.toggle}
            title={condensed ? "Expand stats" : "Collapse stats"}
            className="kpi-band-toggle"
            style={{
              "--kpi-toggle-hover-border": T.kpiToggleHoverBorder,
              flex: condensed ? "0 0 28px" : "0 0 30px",
              width: condensed ? 28 : 30,
              height: condensed ? 26 : undefined,
              alignSelf: condensed ? "center" : "stretch",
              marginInline: condensed ? 4 : 0,
              display: "grid", placeItems: "center", cursor: "pointer",
              borderRadius: condensed ? T.radiusCard : 14,
              boxShadow: condensed ? undefined : T.listCardShadow,
              boxSizing: "border-box",
              padding: 0,
            }}>
            {condensed ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronUp size={16} aria-hidden="true" />}
          </button>
        </div>
        {preference?.error && <div role="alert" style={{ color: T.red, fontSize: T.fsXs, marginTop: 4 }}>
          {preference.error} <span>KPI band choice is not saved.</span>{" "}
          <button type="button" onClick={preference.retry}>Retry saving KPI band choice</button>
        </div>}
      </section>
    </KpiBandCollectionContext.Provider>
    </KpiBandPresentationContext.Provider>
  );
}
